/**
 * Language routing.
 *
 * Each locale is a real URL — `/`, `/zh-hant`, `/zh-hans`, `/ja` — so a visitor
 * can link to, bookmark and share the language they read in, and so crawlers see
 * four documents rather than one. CloudFront rewrites every unmatched path to
 * index.html, and this module reads the language back out of the path.
 */

import { DEFAULT_LANG, LANGS, LANG_PATH, PATH_LANG } from '../content.js';
import { PAGES } from '../legal.js';

/** Every page with its own URL: the policy pages, and paying an invoice. */
export const ROUTES = [...PAGES, 'pay'];

const STORAGE_KEY = 'veni-lang';

/**
 * A path is `/{lang?}/{page?}` — `/`, `/ja`, `/terms`, `/ja/terms`. The
 * language segment is optional because English lives at the root, so the first
 * segment has to be tested as a language before it is treated as a page.
 */
export function routeFromPath(pathname = location.pathname) {
  const [first, second] = pathname.split('/').filter(Boolean).map((s) => s.toLowerCase());
  const lang = (first && PATH_LANG[first]) || null;
  const pageSegment = lang ? second : first;
  const page = ROUTES.includes(pageSegment) ? pageSegment : null;
  return { lang, page };
}

/** The language a path names, or null when it names none. */
export function langFromPath(pathname = location.pathname) {
  return routeFromPath(pathname).lang;
}

/** The page a path names, or null for the landing page. */
export function pageFromPath(pathname = location.pathname) {
  return routeFromPath(pathname).page;
}

/** The stored preference, if it is still a language we publish. */
export function storedLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return LANGS.includes(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function rememberLang(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private browsing — the URL still carries the choice */
  }
}

/** First browser-preferred language we publish. `zh` without a script defaults to Traditional. */
export function browserLang() {
  for (const tag of navigator.languages || [navigator.language || '']) {
    const lower = tag.toLowerCase();
    if (lower.startsWith('ja')) return 'ja';
    if (lower.startsWith('zh')) {
      if (/hans|-cn|-sg|-my/.test(lower)) return 'zh-Hans';
      return 'zh-Hant';
    }
    if (lower.startsWith('en')) return 'en';
  }
  return null;
}

/**
 * The language to render on load: an explicit URL wins, then a remembered
 * choice, then the browser's own preference.
 */
export function initialLang() {
  return langFromPath() || storedLang() || browserLang() || DEFAULT_LANG;
}

export function pathForLang(lang) {
  return LANG_PATH[lang] || LANG_PATH[DEFAULT_LANG];
}

/** `/`, `/terms`, `/ja`, `/ja/terms` — the language prefix is bare for English. */
export function pathFor(lang, page = null) {
  const prefix = pathForLang(lang);
  if (!page) return prefix;
  return prefix === '/' ? `/${page}` : `${prefix}/${page}`;
}

/** Push a route into the URL bar without reloading, preserving any hash. */
export function pushRoute(lang, page = null) {
  const url = pathFor(lang, page) + location.hash;
  if (url !== location.pathname + location.hash) history.pushState({ lang, page }, '', url);
}

export function onRouteChange(handler) {
  window.addEventListener('popstate', () => {
    const { lang, page } = routeFromPath();
    handler(lang || DEFAULT_LANG, page);
  });
}

/**
 * Same-document navigation for links marked data-route. They keep real hrefs,
 * so they stay crawlable and open in a new tab normally; this only saves the
 * reload for a plain click.
 */
export function interceptLinks(root, handler) {
  root.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-route]');
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (link.target === '_blank') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const { lang, page } = routeFromPath(new URL(link.href).pathname);
    handler(lang || DEFAULT_LANG, page);
  });
}
